/**
 * Re-encoding a video picked from the gallery, before it is uploaded.
 *
 * A clip off a phone is enormous for what it is: twenty-four seconds from an
 * Honor arrives as 50 МБ, which is 16.7 Mbps — 1080p at 60fps, or 4K, encoded by
 * a camera that is optimising for the next frame rather than for the file. None
 * of that survives the trip anyway. An album tile is 232px across and a video
 * opened full-screen is the width of a phone, so the pixels past 720p are paid
 * for by whoever is on mobile data and then thrown away by the decoder.
 *
 * So the clip is re-encoded here, on the sender's device, before a byte goes up:
 * 720p at 30fps and 2.5 Mbps, H.264 and AAC, which is the same 24 seconds in
 * about 7.5 МБ. That is not a coincidental number — it is what Telegram gets out
 * of the same file, and the arithmetic is the whole trick rather than anything
 * clever: `24 × (2500 + 96) ÷ 8 ≈ 7.8 МБ`.
 *
 * **Why this is possible now and was not when capture.ts was written.** That
 * file says, correctly for its time, that the only way a browser can re-encode a
 * finished file is to play it back in real time — a minute of waiting to send a
 * minute of video — which is why a кружочек is encoded *while* it is captured.
 * WebCodecs changed the answer: `VideoDecoder` and `VideoEncoder` hand the work
 * to whatever encoder the browser has — on a phone, the same hardware codec the
 * camera app uses — at several times realtime, so a 24-second clip is done in a
 * few seconds rather than in 24. Mediabunny is here for the two things WebCodecs
 * deliberately does not do: demuxing the MP4 that came in and muxing the one
 * going out.
 *
 * A few seconds is still long enough to need saying out loud, which is why this
 * reports progress and the sending bubble counts «Сжимаем %» before it counts
 * «Отправляем %» (see sendAlbum in chat/ChatContext.tsx).
 *
 * **Everything here is allowed to fail.** A codec the phone will not encode, a
 * container mediabunny cannot read, an encoder that dies halfway — each one ends
 * with the original file being sent exactly as it was picked, which is what this
 * app did with every video until now. Compression is an improvement on the
 * upload, never a condition of it.
 */

import type { Input as MediaInput, InputVideoTrack } from 'mediabunny';
import type { OutgoingItem } from '../api/client.ts';
import { clampTunables, type Limit } from './tunables.ts';

/**
 * What a picked video is re-encoded to. Mirrors VideoImportTunables in
 * server/src/settings.ts.
 *
 * Admin settings for the same reason the recording knobs are (see capture.ts):
 * the encoding happens on this device, so they are delivered with the session
 * and spent here. The defaults are Telegram's numbers, because they are the ones
 * everybody has already been trained to find acceptable.
 */
export interface ImportQuality {
  /** The long side of the output frame; the short side follows the aspect ratio. */
  importPx: number;
  importFps: number;
  importKbps: number;
  importAudioKbps: number;
}

export const IMPORT_DEFAULTS: ImportQuality = {
  importPx: 1280,
  importFps: 30,
  importKbps: 2500,
  importAudioKbps: 96,
};

/** The same guard rails the server applies — see LIMITS in server/src/settings.ts. */
export const IMPORT_LIMITS: Record<keyof ImportQuality, Limit> = {
  importPx: { min: 480, max: 1920 },
  importFps: { min: 15, max: 60 },
  importKbps: { min: 500, max: 8000 },
  importAudioKbps: { min: 48, max: 192 },
};

let quality: ImportQuality = { ...IMPORT_DEFAULTS };

export function importQuality(): ImportQuality {
  return quality;
}

export function setImportQuality(next: Partial<ImportQuality>): void {
  quality = clampTunables(quality, next, IMPORT_LIMITS);
}

/**
 * What a compressed clip is expected to weigh a second, sound included.
 *
 * Both halves, because the question this number answers is about the file rather
 * than about the picture: whether re-encoding would make the thing being
 * uploaded any smaller. Container overhead is ignored — it is a couple of
 * percent, and erring towards "compress it" is the cheaper mistake.
 */
export function targetBitsPerSecond(): number {
  return (quality.importKbps + quality.importAudioKbps) * 1000;
}

/**
 * How often a key frame is written, in seconds.
 *
 * Two is the usual compromise and it is worth naming which way each direction
 * hurts: fewer key frames is a smaller file and a video that jumps when it is
 * scrubbed, more is the opposite. Phone cameras sit at the expensive end — short
 * groups of pictures, sometimes every frame — which is a real part of why the
 * file that arrives is as large as it is.
 */
const KEY_FRAME_SECONDS = 2;

/** True when this browser has the two halves of WebCodecs a transcode needs. */
export function canCompressVideo(): boolean {
  return (
    typeof globalThis !== 'undefined' &&
    'VideoEncoder' in globalThis &&
    'VideoDecoder' in globalThis
  );
}

/**
 * The output frame for a source of this shape: the long side pulled down to
 * `longSide`, the aspect ratio kept, and never scaled up — a clip that is
 * already small stays the size it is.
 *
 * Both sides are rounded to even numbers because H.264 stores chroma at half
 * resolution and an odd dimension has nowhere to put the last row.
 */
export function fitInside(
  width: number,
  height: number,
  longSide: number,
): { width: number; height: number } {
  const scale = Math.min(1, longSide / Math.max(width, height));
  const even = (value: number) => Math.max(2, Math.round((value * scale) / 2) * 2);
  return { width: even(width), height: even(height) };
}

/**
 * Whether re-encoding this file would actually make it smaller.
 *
 * Measured as bits a second over the whole file rather than by looking at the
 * resolution, because the resolution is not the question — a 4K clip at a
 * miserly bitrate is already small, and a re-encode would only cost it quality
 * for nothing. Anything already at or under the target is left alone, which is
 * what makes forwarding a clip that came out of this app a copy rather than a
 * second generation of lossy encoding.
 */
export function wouldShrink(bytes: number, durationSeconds: number): boolean {
  if (!(durationSeconds > 0) || bytes <= 0) return false;
  return (bytes * 8) / durationSeconds > targetBitsPerSecond();
}

/**
 * What one picked item is expected to weigh by the time it is uploaded.
 *
 * The compose screen refuses anything over the server's cap before the group is
 * sent (see AlbumCompose), and that check has to be about the file that will
 * actually go up rather than the one that came out of the gallery — otherwise a
 * two-minute clip is refused for being 300 МБ when what would be sent is 40.
 *
 * Deliberately the same arithmetic the encoder is configured with, and
 * deliberately not a promise: a clip whose length never loaded, or that this
 * browser cannot transcode, is counted at the size it already is. Guessing low
 * would move the refusal from a screen that can explain it to a failed upload
 * that cannot.
 */
export function expectedUploadBytes(item: {
  kind: 'photo' | 'video';
  file: File;
  durationMs: number | null;
}): number {
  if (item.kind !== 'video' || !canCompressVideo() || item.durationMs === null) return item.file.size;
  const seconds = item.durationMs / 1000;
  if (!wouldShrink(item.file.size, seconds)) return item.file.size;
  return Math.round((targetBitsPerSecond() * seconds) / 8);
}

/** `holiday.mov` → `holiday.mp4`, since what comes out is always an MP4. */
export function mp4NameFor(name: string): string {
  const dot = name.lastIndexOf('.');
  const stem = dot > 0 ? name.slice(0, dot) : name;
  return `${stem || 'video'}.mp4`;
}

/**
 * The picked file, re-encoded — or the picked file itself.
 *
 * Never throws and never rejects: the return value is the file to upload, and
 * getting the original back is a normal outcome rather than an error. Compare it
 * by identity if you need to know which happened.
 *
 * `onProgress` runs from 0 to 1 over the encode, and only if there is one; a
 * clip that is left alone reports nothing, because there is nothing to wait for.
 */
export async function compressVideo(
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<File> {
  if (!canCompressVideo()) return file;

  /**
   * Imported here rather than at the top of the file, and the reason is a
   * measurement: the demuxer and muxer are ≈170 КБ gzipped against the app's own
   * ≈115, so bundling them would more than double what has to be parsed before a
   * chat list can be drawn — for code most sessions never reach. As its own
   * chunk it is off the boot path entirely, and the first video of a session
   * waits a moment for something it is about to spend seconds encoding anyway.
   *
   * The service worker still precaches the chunk with everything else, so an
   * installed app has it before it is asked for. That is the good half of the
   * trade: out of the critical path, and there when it is wanted.
   */
  let input: MediaInput | null = null;
  try {
    const {
      ALL_FORMATS,
      BlobSource,
      BufferTarget,
      canEncodeVideo,
      Conversion,
      Input,
      Mp4OutputFormat,
      Output,
      Quality,
    } = await import('mediabunny');

    input = new Input({ formats: ALL_FORMATS, source: new BlobSource(file) });
    const track = await input.getPrimaryVideoTrack();
    if (!track) return file;

    // From the demuxer rather than from a <video> element: the container is
    // already open here, and this is the number the decision below turns on.
    const duration = await input.computeDuration();
    if (!wouldShrink(file.size, duration)) return file;

    const frame = fitInside(track.displayWidth, track.displayHeight, quality.importPx);
    // Asked before anything is configured, because "this phone cannot encode
    // H.264 at this size" and "the encode failed" want the same handling but the
    // first one should not cost a decoder start to find out.
    if (!(await canEncodeVideo('avc', { width: frame.width, height: frame.height }))) return file;

    const sourceFps = await sourceFrameRate(track);
    const target = new BufferTarget();
    const conversion = await Conversion.init({
      input,
      output: new Output({
        // The metadata goes at the front. A chat plays videos straight out of an
        // HTTP range request, and an MP4 with its index at the end has to be
        // fetched to the last byte before the first frame can be shown.
        format: new Mp4OutputFormat({ fastStart: 'in-memory' }),
        target,
      }),
      video: {
        ...frame,
        // Stretch to the box, which sounds wrong and is the only correct answer
        // here: the box was computed *from* this track's own aspect ratio (see
        // fitInside), so there is nothing to stretch — at most a rounding to the
        // nearest even pixel. 'contain' would letterbox that rounding into black
        // bars, and 'cover' would crop it off. Naming one is not optional:
        // mediabunny refuses a width and a height without a fit.
        fit: 'fill',
        // A ceiling rather than a rate, and left unset entirely when the source
        // is already slower. Naming one has mediabunny resample to it, which for
        // a 23.976 file measured back as 23.976 is work to arrive where it
        // started — and for anything below the cap it could only add frames.
        ...(sourceFps > quality.importFps ? { frameRate: quality.importFps } : {}),
        // H.264 rather than anything newer, for the same reason capture.ts asks
        // for it first: it is the one codec every phone decodes in hardware.
        codec: 'avc',
        quality: new Quality({ bitrate: quality.importKbps * 1000, bitrateMode: 'variable' }),
        keyFrameInterval: KEY_FRAME_SECONDS,
        // No `hardwareAcceleration` hint, which is not the obvious choice and is
        // the right one. Asking for `prefer-hardware` reads like a preference and
        // is enforced as a requirement: a machine whose browser will not encode
        // H.264 that way refuses the whole configuration, and this file's answer
        // to a refusal is to upload the 50 МБ original. Left unset, the browser
        // picks the hardware encoder where there is one — which on a phone there
        // always is — and falls back to software rather than to nothing. It also
        // keeps this in step with the canEncodeVideo check above, which cannot
        // ask about a hint it was not given.
      },
      audio: {
        codec: 'aac',
        quality: new Quality({ bitrate: quality.importAudioKbps * 1000 }),
      },
      // The discarded tracks are handled below by checking what came out; a
      // console full of warnings about a subtitle track nobody asked for is not.
      showWarnings: false,
    });

    // A file whose video this device cannot decode at all — the tracks were all
    // discarded, and what would come out is an empty container.
    if (!conversion.isValid) return file;
    if (onProgress) conversion.onProgress = (progress) => onProgress(clamp01(progress));

    await conversion.execute();

    const buffer = target.buffer;
    // Bigger than what was picked is a real possibility on a clip that was
    // already efficiently encoded, and the answer to it is the original.
    if (!buffer || buffer.byteLength === 0 || buffer.byteLength >= file.size) return file;

    return new File([buffer], mp4NameFor(file.name), {
      type: 'video/mp4',
      lastModified: file.lastModified,
    });
  } catch (error) {
    console.warn('videoCompress: sending the original', error);
    return file;
  } finally {
    input?.dispose();
  }
}

/**
 * Every video in a picked group, re-encoded, with one fraction counting all of
 * them.
 *
 * Weighted by the bytes each clip started with rather than one step per item:
 * a group of a two-second clip and a two-minute one is not half done when the
 * short one finishes, and a bar that said so would sit at 50% for a minute.
 * Photographs are passed straight through — they are compressed on the server,
 * where there is a decoder for them already (see server/src/images.ts).
 *
 * Sequential on purpose. Encoding is the hardware codec's, and phones have one:
 * running four at once does not finish sooner, it just makes the progress
 * unreadable and the device hot.
 */
export async function compressAlbumVideos(
  items: OutgoingItem[],
  onProgress: (fraction: number) => void,
): Promise<OutgoingItem[]> {
  const total = items.reduce((sum, item) => (item.kind === 'video' ? sum + item.file.size : sum), 0);
  if (total === 0) return items;

  let done = 0;
  const prepared: OutgoingItem[] = [];

  for (const item of items) {
    if (item.kind !== 'video') {
      prepared.push(item);
      continue;
    }
    const weight = item.file.size;
    const file = await compressVideo(item.file, (fraction) =>
      onProgress((done + weight * fraction) / total),
    );
    done += weight;
    // Whole, whatever the encoder last reported — and immediately for a clip
    // that was left alone, which is what makes a skipped item a jump forward
    // rather than a bar that never reaches the end.
    onProgress(done / total);
    // Identity, not a copy: the tile's poster, length and object URL all belong
    // to what the sender picked and none of them change under a re-encode.
    prepared.push(file === item.file ? item : { ...item, file });
  }

  return prepared;
}

function clamp01(value: number): number {
  return Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0;
}

/**
 * The source's frame rate, from a sample of its packets rather than all of them.
 *
 * A hundred frames is a few seconds of any clip and enough to tell 30 from 60,
 * which is the only distinction this is used for. A file that will not answer is
 * treated as fast, so the cap applies — the cap is the safe direction.
 */
async function sourceFrameRate(track: InputVideoTrack): Promise<number> {
  try {
    const stats = await track.computePacketStats(100);
    return stats.averagePacketRate > 0 ? stats.averagePacketRate : Number.POSITIVE_INFINITY;
  } catch {
    return Number.POSITIVE_INFINITY;
  }
}
